import React from 'react'
import FieldConditionalCustomization from './FieldConditionalCustomization'
import { toast } from 'react-toastify'
import SubFormSetup from './SubFormSetup'
import TableFieldPreview from './TableFieldPreview'
import TableRowPrefillConfigPanel from './TableRowPrefillConfigPanel'
import ButtonRowPreview from './ButtonRowPreview'
import { CONDITIONAL_FIELD_TYPES, isTextLikeFieldType, TIMED_FIELD_TYPES } from './formFieldTypes'
import {
  TABLE_MAX_COLS,
  TABLE_MAX_ROWS,
  TABLE_COLUMN_TYPE_OPTIONS,
  clampTableDimension,
  defaultColumnHeaders,
  defaultRowHeaders,
  getColumnTypes,
  resizeColumnTypes,
  resizeHeaders
} from './tableFieldUtils'
import {
  resizeTableColumnPrefillSettings,
  resizeTableRowPrefillSettings,
  getTableRowPrefillSettings,
  type TableRowPrefillSettings
} from './prefillFieldSettingsUtils'
import {
  BUTTON_ACTIONS,
  ButtonConfig,
  createDefaultButton,
  createDefaultPopupFields,
  isButtonRow,
  isPopupButtonAction,
  isStandaloneButton,
  PopupActionButton,
  resolveButtonAction
} from './buttonFieldUtils'
import PopupButtonFieldsEditor from './PopupButtonFieldsEditor'
import PrefillFieldSettings from './PrefillFieldSettings'
interface IProps {
  formElements: any
  setFormElements: any
  selectedField: any
  isFormTimed: any
  setSelectedFields: any
  formBuilderData: any
  showSaveButton: any
  togglerFieldIndex: any
  setTogglerFieldIndex: any
  subFormFieldIndexes: any
  setSubFormFieldIndexes: any
  allFormElements: any
  currentPageIndex: number
  isPrefillForm?: boolean
  selectedTableColumnIndex?: number | null
  onSelectTableColumn?: (columnIndex: number | null) => void
  selectedTableRowIndex?: number | null
  onSelectTableRow?: (rowIndex: number | null) => void
}
const FormFieldCustomization: React.FC<IProps> = ({
  formElements,
  setFormElements,
  selectedField,
  isFormTimed,
  setSelectedFields,
  formBuilderData,
  showSaveButton,
  togglerFieldIndex,
  setTogglerFieldIndex,
  subFormFieldIndexes,
  setSubFormFieldIndexes,
  allFormElements,
  currentPageIndex,
  isPrefillForm = false,
  selectedTableColumnIndex = null,
  onSelectTableColumn,
  selectedTableRowIndex = null,
  onSelectTableRow
}) => {
  let selectedElement = formElements[selectedField]

  // Helper function to update current page in the full structure
  const updateCurrentPage = (updatedPageElements: any) => {
    const newFormElements = [...allFormElements]
    newFormElements[currentPageIndex] = updatedPageElements
    setFormElements(newFormElements)
  }

  // create duplicate field
  const handleDuplicate = () => {
    let lastLeftFieldId = Math.max(...formElements.map((field: any): any => field.fieldId))
    const duplicatedElement = { ...selectedElement, fieldId: lastLeftFieldId + 1 }
    if (duplicatedElement.useAsRecipientName) {
      duplicatedElement.useAsRecipientName = false
    }

    if (selectedElement.type === 'buttonRow') {
      duplicatedElement.buttons = (selectedElement.buttons || []).map(
        (button: any, index: number) => ({
          ...button,
          id: `btn-${duplicatedElement.fieldId}-${index}-${Date.now()}`
        })
      )
    }

    // Types that are NOT allowed to duplicate
    const nonDuplicableTypes = ['banner', 'captcha']

    if (!nonDuplicableTypes.includes(selectedElement.type)) {
      const updatedFormElements = [...formElements]
      const selectedIndex = formElements.findIndex(
        (el: any) => el.fieldId === selectedElement.fieldId
      )

      if (selectedIndex !== -1) {
        const newIndex = selectedField + 1
        updatedFormElements.splice(newIndex, 0, duplicatedElement)
        updateCurrentPage(updatedFormElements)
        toast.success('Duplicate element created successfully!')
      }
    } else {
      toast.warning(`You can't create a duplicate of ${selectedElement.type}.`)
    }
  }

  // delete field
  const handleDelete = () => {
    if (selectedField !== null && formElements.length > 0) {
      const updatedFormElements = formElements.filter(
        (element: any, index: any) => index !== selectedField
      )

      // Adjust selectedField when deleting
      const updatedSelectedField = Math.min(selectedField, updatedFormElements.length - 1)

      updateCurrentPage(updatedFormElements)
      setSelectedFields(updatedSelectedField >= 0 ? updatedSelectedField : null)
      toast.success('Form field deleted successfully!')
    }
  }

  const getMaxFromRule = (rule: any) => {
    // Check if rule is defined before using match
    if (rule) {
      const maxMatch = rule.match(/max-(\d+)/)
      return maxMatch ? maxMatch[1] : ''
    }
    return ''
  }
  const getMinFromRule = (rule: any) => {
    if (rule) {
      const minMatch = rule.match(/min-(\d+)/)
      return minMatch ? minMatch[1] : ''
    }
    return ''
  }
  const getMaxSizeFromRule = (rule: any) => {
    if (rule) {
      const maxFileMatch = rule.match(/maxFileSize-(\d+)/)
      return maxFileMatch ? maxFileMatch[1] : ''
    }
    return ''
  }

  // Function to handle Max File Size change
  const handleMaxSizeChange = (size: any) => {
    const updatedElements = [...formElements]
    const selectedFieldObject = updatedElements[selectedField]

    if (!Object.isExtensible(selectedFieldObject)) {
      updatedElements[selectedField] = { ...selectedFieldObject }
    }

    const validationRule = updatedElements[selectedField].validation?.[0]

    if (validationRule) {
      const newValidationRule = {
        ...validationRule,
        rule:
          size !== ''
            ? `required|max-${getMaxFromRule(validationRule.rule)}|maxFileSize-${size}`
            : `required|max-${getMaxFromRule(validationRule.rule)}|maxFileSize-''`
      }
      updatedElements[selectedField].validation = [newValidationRule]
    }

    updateCurrentPage(updatedElements)
  }

  const isRequired =
    selectedElement?.validation && selectedElement?.validation[0]?.rule.includes('required')
  const label = isRequired ? 'Required' : 'Undefined'
  const className = `mb-3 ${isRequired ? '' : 'd-none'}`

  const isDynamicType = [
    'heading',
    'subheading',
    'formLogo',
    'select',
    'switch',
    'radio',
    'checkbox',
    'file',
    'datetime',
    'date',
    'time',
    'daterange',
    'banner',
    'captcha',
    'ratings',
    'subform'
  ].includes(selectedElement?.type)

  const getLabel = () => {
    return selectedElement?.type === 'banner' || selectedElement?.type === 'formLogo'
      ? 'SrcUrl:'
      : 'Label:'
  }

  // Function to get input value based on element type
  const getInputValue = () => {
    switch (selectedElement?.type) {
      case 'heading':
        return selectedElement?.formHeading
      case 'subheading':
        return selectedElement?.formSubHeading
      case 'formLogo':
        return selectedElement?.imgSrc
      case 'select':
      case 'switch':
      case 'radio':
      case 'checkbox':
      case 'file':
      case 'datetime':
      case 'date':
      case 'time':
      case 'daterange':
      case 'banner':
      case 'captcha':
      case 'ratings':
      case 'subform':
      case 'table':
        return selectedElement?.label
      default:
        return selectedElement?.type
    }
  }

  const handleFieldChange = (index: any, newValue: any, inputSet: any) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[index] }
    updatedElement[inputSet] = newValue
    updatedElements[index] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const handleTableRowPrefillSelect = (rowIndex: number) => {
    const nextRowIndex = selectedTableRowIndex === rowIndex ? null : rowIndex
    onSelectTableRow?.(nextRowIndex)
    onSelectTableColumn?.(null)

    if (nextRowIndex === null) return

    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField] }
    if (updatedElement.prefillEditMode !== 'editFields') {
      updatedElement.prefillEditable = true
      updatedElement.prefillEditMode = 'editFields'
    }
    updatedElement.tableRowPrefillSettings = resizeTableRowPrefillSettings(
      updatedElement.tableRowPrefillSettings,
      updatedElement.tableRowCount ?? 3
    )
    const nextSettings = [...updatedElement.tableRowPrefillSettings]
    nextSettings[rowIndex] = {
      ...nextSettings[rowIndex],
      allowEdit: true
    }
    updatedElement.tableRowPrefillSettings = nextSettings
    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const updateTableRowPrefillSettings = (
    rowIndex: number,
    updates: Partial<TableRowPrefillSettings>
  ) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField] }
    const nextSettings = resizeTableRowPrefillSettings(
      updatedElement.tableRowPrefillSettings,
      updatedElement.tableRowCount ?? 3
    )
    nextSettings[rowIndex] = {
      ...nextSettings[rowIndex],
      ...updates,
      allowEdit: updates.allowEdit ?? nextSettings[rowIndex]?.allowEdit ?? false
    }
    updatedElement.tableRowPrefillSettings = nextSettings
    updatedElement.prefillEditable = true
    updatedElement.prefillEditMode = 'editFields'
    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const handleTableDimensionChange = (
    dimension: 'tableRowCount' | 'tableColumnCount',
    rawValue: string
  ) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField] }
    const max = dimension === 'tableRowCount' ? TABLE_MAX_ROWS : TABLE_MAX_COLS
    const count = clampTableDimension(rawValue, max)
    updatedElement[dimension] = count

    if (dimension === 'tableRowCount') {
      updatedElement.rowHeaders = resizeHeaders(updatedElement.rowHeaders, count, defaultRowHeaders)
      updatedElement.tableRowPrefillSettings = resizeTableRowPrefillSettings(
        updatedElement.tableRowPrefillSettings,
        count
      )
    } else {
      updatedElement.columnHeaders = resizeHeaders(
        updatedElement.columnHeaders,
        count,
        defaultColumnHeaders
      )
      updatedElement.columnTypes = resizeColumnTypes(updatedElement.columnTypes, count)
      updatedElement.tableColumnPrefillSettings = resizeTableColumnPrefillSettings(
        updatedElement.tableColumnPrefillSettings,
        count
      )
    }

    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const handleTableHeaderChange = (
    headerType: 'columnHeaders' | 'rowHeaders',
    headerIndex: number,
    value: string
  ) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField] }
    const headers = [...(updatedElement[headerType] || [])]
    headers[headerIndex] = value
    updatedElement[headerType] = headers
    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const handleTableColumnTypeChange = (columnIndex: number, value: string) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField] }
    const columnTypes = getColumnTypes(updatedElement)
    columnTypes[columnIndex] = value === 'number' || value === 'file' ? value : 'text'
    updatedElement.columnTypes = columnTypes
    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const handleButtonActionChange = (action: string) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField] }
    updatedElement.buttonAction = action
    updatedElement.buttonType =
      action === 'reset' ? 'reset' : action === 'submit' ? 'submit' : 'button'
    if (action === 'next' && updatedElement.confirmBeforeAction === undefined) {
      updatedElement.confirmBeforeAction = true
      updatedElement.confirmTitle = updatedElement.confirmTitle || 'Continue?'
      updatedElement.confirmMessage =
        updatedElement.confirmMessage || 'Are you sure you want to continue to the next page?'
    }
    if (action === 'popup' || action === 'popupWithActions') {
      Object.assign(updatedElement, createDefaultPopupFields(action))
    }
    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const updateButtonRowButtons = (buttons: ButtonConfig[]) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField], buttons }
    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  const handleButtonRowButtonChange = (
    buttonIndex: number,
    field: keyof ButtonConfig,
    value: string | boolean
  ) => {
    const buttons = [...(selectedElement.buttons || [])]
    buttons[buttonIndex] = { ...buttons[buttonIndex], [field]: value }
    if (field === 'buttonAction' && typeof value === 'string') {
      buttons[buttonIndex].buttonType =
        value === 'reset' ? 'reset' : value === 'submit' ? 'submit' : 'button'
      if (value === 'popup' || value === 'popupWithActions') {
        Object.assign(buttons[buttonIndex], createDefaultPopupFields(value))
      }
    }
    updateButtonRowButtons(buttons)
  }

  const labelStatus =
    selectedElement?.includeOnCondition && selectedElement?.includeOnCondition?.conditionActive
      ? 'Enabled'
      : 'Disabled'

  const elementClassName = `mb-3 ${
    selectedElement?.includeOnCondition?.conditionActive ? '' : 'd-none'
  }`
  const labelText =
    selectedElement?.validation && selectedElement.validation[0]?.rule.includes('required')
      ? 'Required'
      : 'Undefined'

  const elementClass = `mb-3 ${
    selectedElement?.validation?.[0]?.rule?.includes('required') ? '' : 'd-none'
  }`

  const updateValidationField = (index: any, newRule: any, newErrorMessage: any) => {
    const updated = [...formElements]
    const selectedFieldObject = updated[index]

    if (!Object.isExtensible(selectedFieldObject)) {
      updated[index] = {
        ...selectedFieldObject,
        validation: [{ rule: newRule, errorMessage: newErrorMessage }]
      }
    } else {
      const validationRule = selectedFieldObject.validation?.[0]

      if (!validationRule) {
        updated[index].validation = [{ rule: newRule, errorMessage: newErrorMessage }]
      } else {
        const updatedValidationRule = {
          rule: newRule ?? validationRule.rule,
          errorMessage: newErrorMessage ?? validationRule.errorMessage
        }
        updated[index].validation = [updatedValidationRule]
      }
    }

    updateCurrentPage(updated)
  }

  const deleteOption = (selectedFieldDelete: any, optionIndex: any) => {
    const updated = [...formElements]
    const updatedElement = { ...updated[selectedFieldDelete] }

    updatedElement.options = [...(updatedElement.options || [])]
    updatedElement.options = updatedElement.options.filter(
      (_: any, index: any) => index !== optionIndex
    )

    updated[selectedFieldDelete] = updatedElement
    updateCurrentPage(updated)
  }

  const addOption = (selectedFieldAdd: any) => {
    const updated = [...formElements]
    const updatedElement = { ...updated[selectedFieldAdd] }
    const nextOptions = [...(updatedElement.options || [])]
    nextOptions.push({
      id: `opt-${updatedElement.fieldId}-${Date.now()}-${nextOptions.length}`,
      text: '',
      value: '',
      selected: false
    })
    updatedElement.options = nextOptions
    updated[selectedFieldAdd] = updatedElement
    updateCurrentPage(updated)
  }

  const handleValidationChange = (event: any, type: any) => {
    const validationRule = formElements[selectedField].validation?.[0]
    if (type === 'toggle' || type === 'text') {
      const isRuleUndefined = validationRule?.rule.includes('undefined')
      updateValidationField(
        selectedField,
        isRuleUndefined ? 'required' : 'undefined',
        isRuleUndefined ? 'null' : null
      )
    } else if (type === 'message') {
      const newErrorMessage = event.target.value
      updateValidationField(
        selectedField,
        validationRule ? validationRule.rule : 'required',
        newErrorMessage
      )
    }
  }

  const updateValidationRule = (prev: any, selectedField: any, type: any, value: any) => {
    const updated = [...prev]
    const selectedFieldObject = updated[selectedField]

    if (!Object.isExtensible(selectedFieldObject)) {
      updated[selectedField] = { ...selectedFieldObject }
    }

    const validationRule = updated[selectedField].validation?.[0]

    if (validationRule) {
      const minValue = getMinFromRule(validationRule.rule)
      const maxValue = getMaxFromRule(validationRule.rule)

      const newValidationRule = {
        ...validationRule,
        rule:
          type === 'max'
            ? `required|max-${value}|min-${minValue}`
            : `required|max-${maxValue}|min-${value}`
      }
      updated[selectedField].validation = [newValidationRule]
    }

    return updated
  }

  const handleOptionChange = (e: any, index: any) => {
    const { value } = e.target
    const updated = [...formElements]
    const updatedElement = { ...updated[selectedField] }
    const updatedOptions = [...(updatedElement?.options || [])]
    updatedOptions[index] = {
      ...updatedOptions[index],
      text: value,
      ...(selectedElement.type === 'select' ? { value } : {})
    }
    updatedElement.options = updatedOptions
    updated[selectedField] = updatedElement
    updateCurrentPage(updated)
  }

  const getOptionKey = (option: any, optionIndex: number) =>
    option?.id ?? `${selectedElement.fieldId}-option-${optionIndex}`

  const handleDisableToggle = (isChecked: boolean) => {
    const updatedElements = [...formElements]
    const updatedElement = { ...updatedElements[selectedField], isDisabled: isChecked }
    updatedElements[selectedField] = updatedElement
    updateCurrentPage(updatedElements)
  }

  return (
    <>
      <div className='mt-5'>
        {selectedElement ? (
          <>
            <h4 className='text-center'> {selectedElement?.type?.toUpperCase()} CONTENT</h4>
            <hr />
            {/* THE DUPLICATE & DELETE SECTION */}
            <div>
              {selectedElement.type !== 'banner' && selectedElement.type !== 'captcha' && (
                <button className='btn btn-primary w-100 m-1' onClick={handleDuplicate}>
                  Duplicate
                </button>
              )}
              <button className='btn btn-danger w-100 m-1' onClick={handleDelete}>
                Delete
              </button>
            </div>

            {/* THE OPTIONS SECTION */}
            <div className='mt-5'>
              <h5>OPTIONS:</h5>

              {/* setter & getter for label */}
              {isTextLikeFieldType(selectedElement?.type) && (
                <div>
                  <div className='mb-3'>
                    <div className='form-check form-switch'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='showLabelToggle'
                        checked={!selectedElement?.hideLabel}
                        onChange={(e) =>
                          handleFieldChange(selectedField, !e.target.checked, 'hideLabel')
                        }
                      />
                      <label className='form-check-label' htmlFor='showLabelToggle'>
                        Show label
                      </label>
                    </div>
                  </div>
                  {!selectedElement?.hideLabel && (
                    <div className='mb-3'>
                      <label className='form-label'>Label:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement?.label}
                        onChange={(e) => handleFieldChange(selectedField, e.target.value, 'label')}
                      />
                    </div>
                  )}
                  <div className='mb-3'>
                    <label className='form-label'>Placeholder:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement?.placeholder}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'placeholder')
                      }
                    />
                  </div>
                  {/* Exclude color for text type */}
                  <div className='mb-3'>
                    <label className='form-label'>Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement?.color}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'color')}
                    />
                  </div>
                  <div className='mt-5'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='toggleSwitch'
                            checked={
                              selectedElement?.validation &&
                              selectedElement?.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='toggleSwitch'>
                            {label}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={className}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>

                    <div className={className}>
                      <label className='form-label'>Max:</label>
                      <input
                        type='number'
                        className='form-control'
                        value={getMaxFromRule(selectedElement?.validation?.[0]?.rule)}
                        onChange={(e) => {
                          const updatedElements = updateValidationRule(
                            formElements,
                            selectedField,
                            'max',
                            e.target.value
                          )
                          updateCurrentPage(updatedElements)
                        }}
                      />
                    </div>

                    <div className={className}>
                      <label className='form-label'>Min:</label>
                      <input
                        type='number'
                        className='form-control'
                        value={getMinFromRule(selectedElement?.validation?.[0]?.rule)}
                        onChange={(e) => {
                          const updatedElements = updateValidationRule(
                            formElements,
                            selectedField,
                            'min',
                            e.target.value
                          )
                          updateCurrentPage(updatedElements)
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {selectedElement?.type === 'number' && (
                <div>
                  <div className='mb-3'>
                    <label className='form-label'>Label:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement?.label}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'label')}
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Placeholder:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement?.placeholder}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'placeholder')
                      }
                    />
                  </div>
                  {/* Exclude color for text type */}
                  <div className='mb-3'>
                    <label className='form-label'>Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement?.color}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'color')}
                    />
                  </div>
                  <div className='mt-5'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='toggleSwitch'
                            checked={
                              selectedElement?.validation &&
                              selectedElement?.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='toggleSwitch'>
                            {label}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={className}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>

                    <div className={className}>
                      <label className='form-label'>Max:</label>
                      <input
                        type='number'
                        className='form-control'
                        value={getMaxFromRule(selectedElement?.validation?.[0]?.rule)}
                        onChange={(e) => {
                          const updatedElements = updateValidationRule(
                            formElements,
                            selectedField,
                            'max',
                            e.target.value
                          )
                          updateCurrentPage(updatedElements)
                        }}
                      />
                    </div>

                    <div className={className}>
                      <label className='form-label'>Min:</label>
                      <input
                        type='number'
                        className='form-control'
                        value={getMinFromRule(selectedElement?.validation?.[0]?.rule)}
                        onChange={(e) => {
                          const updatedElements = updateValidationRule(
                            formElements,
                            selectedField,
                            'min',
                            e.target.value
                          )
                          updateCurrentPage(updatedElements)
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isDynamicType && (
                <div>
                  {/* label/srcUrl: v(pick values) from formHeading, formSubHeading, imgSrc, label & setting up of their respective ^(set) */}
                  <div className='mb-3'>
                    <label className='form-label'>{getLabel()}</label>
                    <input
                      type='text'
                      className='form-control'
                      value={getInputValue()}
                      onChange={(e) => {
                        const updatedElements = [...formElements]
                        const updatedElement = { ...updatedElements[selectedField] }

                        if (selectedElement.type === 'heading') {
                          updatedElement.formHeading = e.target.value
                        } else if (selectedElement.type === 'subheading') {
                          updatedElement.formSubHeading = e.target.value
                        } else if (
                          selectedElement.type === 'formLogo' ||
                          selectedElement.type === 'select' ||
                          selectedElement.type === 'switch' ||
                          selectedElement.type === 'radio' ||
                          selectedElement.type === 'checkbox' ||
                          selectedElement.type === 'file' ||
                          selectedElement.type === 'datetime' ||
                          selectedElement.type === 'date' ||
                          selectedElement.type === 'time' ||
                          selectedElement.type === 'daterange' ||
                          selectedElement.type === 'banner' ||
                          selectedElement.type === 'captcha' ||
                          selectedElement.type === 'subform' ||
                          selectedElement.type === 'ratings' ||
                          selectedElement.type === 'table'
                        ) {
                          updatedElement.label = e.target.value

                          if (selectedElement.type === 'banner') {
                            updatedElement.bannerSrc = e.target.value
                          } else if (selectedElement.type === 'formLogo') {
                            updatedElement.imgSrc = e.target.value
                          }
                        }

                        updatedElements[selectedField] = updatedElement
                        updateCurrentPage(updatedElements) // Use the updateCurrentPage function instead
                      }}
                    />
                  </div>
                  {/* color: v(pick) & ^(set) values */}
                  {selectedElement?.type !== 'formLogo' &&
                    selectedElement?.type !== 'banner' && ( // Exclude color for formLogo
                      <div className='mb-3'>
                        <label className='form-label'>Color:</label>
                        <input
                          type='color'
                          className='form-control form-control-color'
                          value={selectedElement?.color}
                          onChange={(e) =>
                            handleFieldChange(selectedField, e.target.value, 'color')
                          }
                        />
                      </div>
                    )}

                  {/* size, alignment: v(pick) & ^(set) values */}
                  {(selectedElement.type === 'heading' ||
                    selectedElement.type === 'subheading' ||
                    selectedElement.type === 'formLogo' ||
                    selectedElement.type === 'ratings') && (
                    <>
                      {selectedElement?.type === 'heading' && (
                        <div className='mb-3'>
                          <label className='form-label'>Size:</label>
                          <select
                            className='form-select'
                            value={selectedElement.size}
                            onChange={(e) =>
                              handleFieldChange(selectedField, e.target.value, 'size')
                            }
                          >
                            <option value='h1'>H1</option>
                            <option value='h2'>H2</option>
                            <option value='h3'>H3</option>
                            <option value='h4'>H4</option>
                            <option value='h5'>H5</option>
                            <option value='h6'>H6</option>
                          </select>
                        </div>
                      )}

                      <div className='mb-3'>
                        <label className='form-label'>Alignment:</label>
                        <select
                          className='form-select'
                          value={selectedElement.alignment}
                          onChange={(e) =>
                            handleFieldChange(selectedField, e.target.value, 'alignment')
                          }
                        >
                          <option value='left'>Left</option>
                          <option value='center'>Center</option>
                          <option value='right'>Right</option>
                        </select>
                      </div>

                      {selectedElement?.type === 'formLogo' && (
                        <>
                          <div className='mb-3'>
                            <label className='form-label'>Height (px):</label>
                            <input
                              type='number'
                              min={1}
                              className='form-control'
                              value={selectedElement.height ?? ''}
                              placeholder='e.g. 100'
                              onChange={(e) =>
                                handleFieldChange(
                                  selectedField,
                                  e.target.value === '' ? undefined : Number(e.target.value),
                                  'height'
                                )
                              }
                            />
                          </div>
                          <div className='mb-3'>
                            <label className='form-label'>Width (px):</label>
                            <input
                              type='number'
                              min={1}
                              className='form-control'
                              value={selectedElement.width ?? ''}
                              placeholder='e.g. 150'
                              onChange={(e) =>
                                handleFieldChange(
                                  selectedField,
                                  e.target.value === '' ? undefined : Number(e.target.value),
                                  'width'
                                )
                              }
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
              {selectedElement.type !== 'table' && (
                <PrefillFieldSettings
                  isPrefillForm={isPrefillForm}
                  selectedElement={selectedElement}
                  selectedField={selectedField}
                  currentPageIndex={currentPageIndex}
                  setFormElements={setFormElements}
                  selectedTableColumnIndex={selectedTableColumnIndex}
                  onSelectTableColumn={onSelectTableColumn}
                />
              )}
              {CONDITIONAL_FIELD_TYPES.includes(selectedElement.type) && (
                <div className='bg-success p-2 text-dark bg-opacity-50'>
                  <div className='d-flex justify-content-between'>
                    <h5>Render Conditionally</h5>
                    <div className='mb-5'>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='toggleSwitch'
                          checked={selectedElement?.includeOnCondition?.conditionActive}
                          onChange={() => {
                            setFormElements((prev: any) => {
                              const updated = [...prev]
                              const currentPage = [...updated[currentPageIndex]]
                              const field = currentPage[selectedField]
                              const nextActive = !(
                                currentPage[selectedField]?.includeOnCondition?.conditionActive ||
                                false
                              )

                              if (!Object.isExtensible(field)) {
                                currentPage[selectedField] = { ...field }
                              }

                              currentPage[selectedField].includeOnCondition = {
                                ...(currentPage[selectedField].includeOnCondition || {}),
                                conditionActive: nextActive,
                                basedOnElementOfIndex: nextActive
                                  ? currentPage[selectedField].fieldId
                                  : currentPage[selectedField].includeOnCondition
                                      ?.basedOnElementOfIndex,
                                comparisionOperation:
                                  currentPage[selectedField].includeOnCondition
                                    ?.comparisionOperation || '='
                              }

                              updated[currentPageIndex] = currentPage
                              return updated
                            })
                            if (
                              formElements?.[currentPageIndex]?.[selectedField]?.includeOnCondition
                                ?.conditionActive === false
                            ) {
                              setTogglerFieldIndex(-1)
                            } else {
                              setTogglerFieldIndex(selectedElement.fieldId)
                            }
                          }}
                        />
                        <label className='form-check-label' htmlFor='toggleSwitch'>
                          {labelStatus}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className={elementClassName}>
                    <FieldConditionalCustomization
                      setFormElements={setFormElements}
                      selectedElement={selectedElement}
                      selectedField={selectedField}
                      setTogglerFieldIndex={setTogglerFieldIndex}
                      currentPageIndex={currentPageIndex}
                    />
                  </div>
                </div>
              )}
              {TIMED_FIELD_TYPES.includes(selectedElement?.type) && (
                <div className='mb-3 mt-4'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <label className='form-label mb-0'>Enable Time:</label>
                    <div className='form-check form-switch'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='timeToggle'
                        disabled={isFormTimed === true}
                        checked={selectedElement?.isTime || false}
                        onChange={(e) =>
                          handleFieldChange(selectedField, e.target.checked, 'isTime')
                        }
                      />
                      <label
                        className='form-check-label'
                        htmlFor='timeToggle'
                        title={isFormTimed === true ? 'Disabled due to form timer' : ''}
                      >
                        {isFormTimed === true
                          ? 'Disabled'
                          : selectedElement?.isTime
                          ? 'Enabled'
                          : 'Disabled'}
                      </label>
                    </div>
                  </div>
                  {selectedElement?.isTime && (
                    <div>
                      <label className='form-label'>Time:</label>
                      <input
                        type='time'
                        className='form-control'
                        value={selectedElement?.time || ''}
                        onChange={(e) => handleFieldChange(selectedField, e.target.value, 'time')}
                      />
                    </div>
                  )}
                </div>
              )}
              {selectedElement.type === 'subform' && (
                <>
                  <div className='bg-secondary p-2 text-dark bg-opacity-50'>
                    <div className='mb-3'>
                      <SubFormSetup
                        formElements={formElements} // Current page elements
                        setFormElements={setFormElements} // Updates the entire structure
                        selectedElement={selectedElement}
                        selectedField={selectedField}
                        setTogglerFieldIndex={setTogglerFieldIndex}
                        setSubFormFieldIndexes={setSubFormFieldIndexes}
                        allFormElements={allFormElements} // Pass the full structure
                        currentPageIndex={currentPageIndex} // Pass current page index
                      />
                    </div>
                  </div>
                </>
              )}
              {selectedElement.type === 'table' && (
                <div className='bg-secondary p-2 text-dark bg-opacity-50'>
                  <div className='mb-3'>
                    <label className='form-label'>Label:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.label}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'label')}
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement.color}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'color')}
                    />
                  </div>
                  <div className='row g-3 mb-3'>
                    <div className='col-6'>
                      <label className='form-label'>Rows:</label>
                      <input
                        type='number'
                        min={1}
                        max={TABLE_MAX_ROWS}
                        className='form-control'
                        value={selectedElement.tableRowCount ?? 3}
                        onChange={(e) =>
                          handleTableDimensionChange('tableRowCount', e.target.value)
                        }
                      />
                    </div>
                    <div className='col-6'>
                      <label className='form-label'>Columns:</label>
                      <input
                        type='number'
                        min={1}
                        max={TABLE_MAX_COLS}
                        className='form-control'
                        value={selectedElement.tableColumnCount ?? 3}
                        onChange={(e) =>
                          handleTableDimensionChange('tableColumnCount', e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className='mb-3'>
                    <div className='form-check form-switch'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='tableShowRowHeaders'
                        checked={selectedElement.showRowHeaders === true}
                        onChange={(e) =>
                          handleFieldChange(selectedField, e.target.checked, 'showRowHeaders')
                        }
                      />
                      <label className='form-check-label' htmlFor='tableShowRowHeaders'>
                        Show row headers
                      </label>
                    </div>
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Columns:</label>
                    <div className='row g-2 mb-1'>
                      <div className='col-7'>
                        <small className='text-muted'>Column name</small>
                      </div>
                      <div className='col-5'>
                        <small className='text-muted'>Type</small>
                      </div>
                    </div>
                    {getColumnTypes(selectedElement).map((columnType, index) => (
                      <div
                        key={`table-col-${selectedElement.fieldId}-${index}`}
                        className='row g-2 mb-2 align-items-center'
                      >
                        <div className='col-7'>
                          <input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder={`Column ${index + 1}`}
                            value={(selectedElement.columnHeaders || [])[index] || ''}
                            onChange={(e) =>
                              handleTableHeaderChange('columnHeaders', index, e.target.value)
                            }
                          />
                        </div>
                        <div className='col-5'>
                          <select
                            className='form-select form-select-sm'
                            value={columnType}
                            onChange={(e) => handleTableColumnTypeChange(index, e.target.value)}
                          >
                            {TABLE_COLUMN_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedElement.showRowHeaders || isPrefillForm ? (
                    <div className='mb-3'>
                      <label className='form-label'>Row headers:</label>
                      {(selectedElement.rowHeaders || []).map((header: string, index: number) => {
                        const rowConfig = getTableRowPrefillSettings(selectedElement, index)
                        const fieldCount = rowConfig.prefillEditFields?.length || 0
                        const isExpanded = selectedTableRowIndex === index

                        return (
                          <div
                            key={`table-row-${selectedElement.fieldId}-${index}`}
                            className='mb-2'
                          >
                            <div className='input-group'>
                              {isPrefillForm && fieldCount > 0 ? (
                                <span
                                  className='input-group-text bg-warning text-dark px-2'
                                  title={`${fieldCount} conditional edit field${
                                    fieldCount === 1 ? '' : 's'
                                  }`}
                                >
                                  {fieldCount}
                                </span>
                              ) : null}
                              <input
                                type='text'
                                className='form-control'
                                value={header}
                                onChange={(e) =>
                                  handleTableHeaderChange('rowHeaders', index, e.target.value)
                                }
                              />
                              {isPrefillForm ? (
                                <button
                                  type='button'
                                  className={`btn ${
                                    isExpanded ? 'btn-warning' : 'btn-outline-primary'
                                  }`}
                                  title='Configure edit for this row'
                                  onClick={() => handleTableRowPrefillSelect(index)}
                                >
                                  <i className='bi bi-pencil-square me-1' />
                                  Allow edit
                                </button>
                              ) : null}
                            </div>
                            {isPrefillForm && isExpanded ? (
                              <TableRowPrefillConfigPanel
                                element={selectedElement}
                                rowIndex={index}
                                rowLabel={header}
                                onUpdateRowSettings={updateTableRowPrefillSettings}
                              />
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                  <div className='mb-3'>
                    <label className='form-label'>Preview:</label>
                    <TableFieldPreview
                      element={selectedElement}
                      isPrefillForm={isPrefillForm}
                      selectedRowIndex={selectedTableRowIndex}
                      onRowSelect={handleTableRowPrefillSelect}
                    />
                  </div>
                  <div className='mt-4'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='tableValidationToggle'
                            checked={
                              selectedElement.validation &&
                              selectedElement.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='tableValidationToggle'>
                            {labelText}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={elementClass}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>
                  </div>
                </div>
              )}
              {selectedElement.type === 'select' && (
                <>
                  <div>
                    {!!selectedElement.options &&
                      selectedElement.options.map((option: any, optionIndex: any) => (
                        <div key={getOptionKey(option, optionIndex)} className='mb-3'>
                          <label className='form-label'>Option {optionIndex + 1}:</label>
                          <div className='input-group'>
                            <input
                              type='text'
                              className='form-control'
                              value={option.text}
                              onChange={(e) => handleOptionChange(e, optionIndex)}
                            />
                            <button
                              className='btn btn-danger'
                              type='button'
                              onClick={() => deleteOption(selectedField, optionIndex)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    <button
                      className='btn btn-success'
                      type='button'
                      onClick={() => addOption(selectedField)}
                    >
                      Add Option
                    </button>
                  </div>
                  <div className='mt-5'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='toggleSwitch'
                            checked={
                              selectedElement.validation &&
                              selectedElement.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='toggleSwitch'>
                            {labelText}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={elementClass}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>
                  </div>
                </>
              )}
              {selectedElement.type === 'checkbox' && (
                <>
                  <div>
                    {!!selectedElement.options &&
                      selectedElement.options.map((option: any, optionIndex: any) => (
                        <div key={getOptionKey(option, optionIndex)} className='mb-3'>
                          <label className='form-label'>Option {optionIndex + 1}:</label>
                          <div className='input-group'>
                            <input
                              type='text'
                              className='form-control'
                              value={option.text}
                              onChange={(e) => handleOptionChange(e, optionIndex)}
                            />
                            <button
                              className='btn btn-danger'
                              type='button'
                              onClick={() => deleteOption(selectedField, optionIndex)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    <button
                      className='btn btn-success'
                      type='button'
                      onClick={() => addOption(selectedField)}
                    >
                      Add Option
                    </button>
                    <div className='mt-5'>
                      <div className='d-flex justify-content-between'>
                        <h5>isRequired</h5>
                        <div className='mb-5'>
                          <div className='form-check form-switch'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='toggleSwitch'
                              checked={
                                selectedElement.validation &&
                                selectedElement.validation[0]?.rule.includes('required')
                              }
                              onChange={(e) => handleValidationChange(e, 'toggle')}
                            />
                            <label className='form-check-label' htmlFor='toggleSwitch'>
                              {labelText}
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className={elementClass}>
                        <label className='form-label'>Validation Message:</label>
                        <input
                          type='text'
                          className='form-control'
                          value={selectedElement.validation?.[0]?.errorMessage || ''}
                          onChange={(e) => handleValidationChange(e, 'message')}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              {selectedElement.type === 'divider' && (
                <div>
                  <div className='mb-3'>
                    <label className='form-label'>Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement.color}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'color')}
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Thickness (in pixels):</label>
                    <input
                      type='number'
                      className='form-control'
                      value={selectedElement.thickness}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'thickness')
                      }
                    />
                  </div>
                </div>
              )}
              {selectedElement.type === 'textarea' && (
                <div>
                  <div className='mb-3'>
                    <label className='form-label'>Label:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.label}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'label')}
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Placeholder:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.placeholder}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'placeholder')
                      }
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement.color}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'color')}
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Rows:</label>
                    <input
                      type='number'
                      className='form-control'
                      value={selectedElement.rows}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'rows')}
                    />
                  </div>
                  <div className='mt-5'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='toggleSwitch'
                            checked={
                              selectedElement.validation &&
                              selectedElement.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='toggleSwitch'>
                            {label}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={className}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>
                    <div className={className}>
                      <label className='form-label'>Max:</label>
                      <input
                        type='number'
                        className='form-control'
                        value={getMaxFromRule(selectedElement.validation?.[0]?.rule)}
                        onChange={(e) => {
                          const updatedElements = updateValidationRule(
                            formElements,
                            selectedField,
                            'max',
                            e.target.value
                          )
                          updateCurrentPage(updatedElements)
                        }}
                      />
                    </div>

                    <div className={className}>
                      <label className='form-label'>Min:</label>
                      <input
                        type='number'
                        className='form-control'
                        value={getMinFromRule(selectedElement.validation?.[0]?.rule)}
                        onChange={(e) => {
                          const updatedElements = updateValidationRule(
                            formElements,
                            selectedField,
                            'min',
                            e.target.value
                          )
                          updateCurrentPage(updatedElements)
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {selectedElement.type === 'radio' && (
                <div>
                  {!!selectedElement.options &&
                    selectedElement.options.map((option: any, optionIndex: any) => (
                      <div key={getOptionKey(option, optionIndex)} className='mb-3'>
                        <label className='form-label'>Option {optionIndex + 1}:</label>
                        <div className='input-group'>
                          <input
                            type='text'
                            className='form-control'
                            value={option.text}
                            onChange={(e) => handleOptionChange(e, optionIndex)}
                          />
                          <button
                            className='btn btn-danger'
                            type='button'
                            onClick={() => deleteOption(selectedField, optionIndex)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  <button
                    className='btn btn-success'
                    type='button'
                    onClick={() => addOption(selectedField)}
                  >
                    Add Option
                  </button>
                  <div className='mt-5'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='toggleSwitch'
                            checked={
                              selectedElement.validation &&
                              selectedElement.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='toggleSwitch'>
                            {label}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={className}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>
                  </div>
                  <div className='mt-4'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <h5>DISABLED</h5>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='disableSwitch'
                          checked={!!selectedElement.isDisabled}
                          onChange={(e) => handleDisableToggle(e.target.checked)}
                        />
                        <label className='form-check-label' htmlFor='disableSwitch'>
                          {selectedElement.isDisabled ? 'Disabled' : 'Enabled'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {selectedElement.type === 'captcha' && (
                <div>
                  <div className='mb-3'>
                    <label className='form-label'>Enter Text:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.inputTextName}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'inputTextName')
                      }
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>Text Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement.inputTextNameColor}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'inputTextNameColor')
                      }
                    />
                  </div>
                  <div className='mt-5'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='toggleSwitch'
                            checked={
                              selectedElement.validation &&
                              selectedElement.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='toggleSwitch'>
                            {selectedElement.validation &&
                            selectedElement.validation[0]?.rule.includes('required')
                              ? 'Required'
                              : 'Undefined'}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={className}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>
                  </div>
                </div>
              )}
              {selectedElement.type === 'signature' && (
                <div>
                  <div className='mb-3'>
                    <label className='form-label'>Label:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.label}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'label')}
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label'>InkColor:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement.color}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'color')}
                    />
                    <label className='form-label'>PadColor:</label>
                    <input
                      type='color'
                      className='form-control form-control-color'
                      value={selectedElement.padColor}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'padColor')}
                    />
                  </div>
                  <div className='mb-3 d-grid'>
                    <label className='form-label'>Width:</label>
                    <input
                      type='range'
                      min='300'
                      max='1000'
                      value={selectedElement.width}
                      className='slider'
                      id='myRange'
                      onChange={(e) =>
                        handleFieldChange(selectedField, Number(e.target.value), 'width')
                      }
                    />
                    <label className='form-label'>height:</label>
                    <input
                      type='range'
                      min='100'
                      max='500'
                      value={selectedElement.height}
                      className='slider'
                      id='myRange'
                      onChange={(e) =>
                        handleFieldChange(selectedField, Number(e.target.value), 'height')
                      }
                    />
                  </div>

                  <div className='mt-5'>
                    <div className='d-flex justify-content-between'>
                      <h5>isRequired</h5>
                      <div className='mb-5'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='toggleSwitch'
                            checked={
                              selectedElement.validation &&
                              selectedElement.validation[0]?.rule.includes('required')
                            }
                            onChange={(e) => handleValidationChange(e, 'toggle')}
                          />
                          <label className='form-check-label' htmlFor='toggleSwitch'>
                            {selectedElement.validation &&
                            selectedElement.validation[0]?.rule.includes('required')
                              ? 'Required'
                              : 'Undefined'}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={className}>
                      <label className='form-label'>Validation Message:</label>
                      <input
                        type='text'
                        className='form-control'
                        value={selectedElement.validation?.[0]?.errorMessage || ''}
                        onChange={(e) => handleValidationChange(e, 'message')}
                      />
                    </div>
                  </div>
                </div>
              )}
              {selectedElement.type === 'file' && (
                <div className='mt-5'>
                  <div className='d-flex justify-content-between'>
                    <h5>isRequired</h5>
                    <div className='mb-5'>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='toggleSwitch'
                          checked={
                            selectedElement.validation &&
                            selectedElement.validation[0]?.rule.includes('required')
                          }
                          onChange={(e) => handleValidationChange(e, 'toggle')}
                        />
                        <label className='form-check-label' htmlFor='toggleSwitch'>
                          {selectedElement.validation &&
                          selectedElement.validation[0]?.rule.includes('required')
                            ? 'Required'
                            : 'Undefined'}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className={className}>
                    <label className='form-label'>Validation Message:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.validation?.[0]?.errorMessage || ''}
                      onChange={(e) => handleValidationChange(e, 'message')}
                    />
                  </div>

                  {/* New file size validation */}
                  <div className={className}>
                    <label className='form-label'>Max File Size (In Bytes):</label>
                    <input
                      type='number'
                      className='form-control'
                      value={getMaxSizeFromRule(selectedElement.validation?.[0]?.rule)}
                      onChange={(e) => handleMaxSizeChange(e.target.value)}
                    />
                  </div>
                </div>
              )}
              {selectedElement.type === 'date' && (
                <div className='mt-5'>
                  <div className='d-flex justify-content-between'>
                    <h5>isRequired</h5>
                    <div className='mb-5'>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='toggleSwitch'
                          checked={
                            selectedElement.validation &&
                            selectedElement.validation[0]?.rule.includes('required')
                          }
                          onChange={(e) => handleValidationChange(e, 'toggle')}
                        />
                        <label className='form-check-label' htmlFor='toggleSwitch'>
                          {selectedElement.validation &&
                          selectedElement.validation[0]?.rule.includes('required')
                            ? 'Required'
                            : 'Undefined'}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className={className}>
                    <label className='form-label'>Validation Message:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.validation?.[0]?.errorMessage || ''}
                      onChange={(e) => handleValidationChange(e, 'message')}
                    />
                  </div>
                </div>
              )}
              {selectedElement.type === 'time' && (
                <div className='mt-5'>
                  <div className='d-flex justify-content-between'>
                    <h5>isRequired</h5>
                    <div className='mb-5'>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='toggleSwitch'
                          checked={
                            selectedElement.validation &&
                            selectedElement.validation[0]?.rule.includes('required')
                          }
                          onChange={(e) => handleValidationChange(e, 'toggle')}
                        />
                        <label className='form-check-label' htmlFor='toggleSwitch'>
                          {selectedElement.validation &&
                          selectedElement.validation[0]?.rule.includes('required')
                            ? 'Required'
                            : 'Undefined'}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className={className}>
                    <label className='form-label'>Validation Message:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.validation?.[0]?.errorMessage || ''}
                      onChange={(e) => handleValidationChange(e, 'message')}
                    />
                  </div>
                </div>
              )}
              {selectedElement.type === 'datetime' && (
                <div className='mt-5'>
                  <div className='d-flex justify-content-between'>
                    <h5>isRequired</h5>
                    <div className='mb-5'>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='toggleSwitch'
                          checked={
                            selectedElement.validation &&
                            selectedElement.validation[0]?.rule.includes('required')
                          }
                          onChange={(e) => handleValidationChange(e, 'toggle')}
                        />
                        <label className='form-check-label' htmlFor='toggleSwitch'>
                          {selectedElement.validation &&
                          selectedElement.validation[0]?.rule.includes('required')
                            ? 'Required'
                            : 'Undefined'}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className={className}>
                    <label className='form-label'>Validation Message:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.validation?.[0]?.errorMessage || ''}
                      onChange={(e) => handleValidationChange(e, 'message')}
                    />
                  </div>
                </div>
              )}
              {selectedElement.type === 'daterange' && (
                <div className='mt-5'>
                  <div className='d-flex justify-content-between'>
                    <h5>isRequired</h5>
                    <div className='mb-5'>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='toggleSwitch'
                          checked={
                            selectedElement.validation &&
                            selectedElement.validation[0]?.rule.includes('required')
                          }
                          onChange={(e) => handleValidationChange(e, 'toggle')}
                        />
                        <label className='form-check-label' htmlFor='toggleSwitch'>
                          {selectedElement.validation &&
                          selectedElement.validation[0]?.rule.includes('required')
                            ? 'Required'
                            : 'Undefined'}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className={className}>
                    <label className='form-label'>Validation Message:</label>
                    <input
                      type='text'
                      className='form-control'
                      value={selectedElement.validation?.[0]?.errorMessage || ''}
                      onChange={(e) => handleValidationChange(e, 'message')}
                    />
                  </div>
                </div>
              )}
              {(isStandaloneButton(selectedElement) ||
                selectedElement.type === 'nextPageButton' ||
                selectedElement.type === 'prevPageButton') && (
                <div>
                  <div className='mb-3'>
                    <label className='form-label fw-bold'>Button type:</label>
                    <select
                      className='form-select form-select-sm'
                      value={resolveButtonAction(selectedElement)}
                      onChange={(e) => handleButtonActionChange(e.target.value)}
                    >
                      {BUTTON_ACTIONS.map((action) => (
                        <option key={action.value} value={action.value}>
                          {action.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='mb-3'>
                    <label className='form-label fw-bold'>Button Text:</label>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      value={selectedElement.text || ''}
                      onChange={(e) => handleFieldChange(selectedField, e.target.value, 'text')}
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label fw-bold'>Text Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color form-control-sm'
                      value={selectedElement.btnTxtColor || '#FFFFFF'}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'btnTxtColor')
                      }
                    />
                  </div>
                  <div className='mb-3'>
                    <label className='form-label fw-bold'>Button Color:</label>
                    <input
                      type='color'
                      className='form-control form-control-color form-control-sm'
                      value={selectedElement.btnBgColor || '#000000'}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'btnBgColor')
                      }
                    />
                  </div>
                  {isPopupButtonAction(resolveButtonAction(selectedElement)) ? (
                    <PopupButtonFieldsEditor
                      config={selectedElement}
                      onFieldChange={(field, value) =>
                        handleFieldChange(selectedField, value, field)
                      }
                    />
                  ) : null}
                  {['next', 'submit', 'previous', 'reset'].includes(
                    resolveButtonAction(selectedElement)
                  ) && (
                    <>
                      <div className='mb-3'>
                        <div className='form-check form-switch'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='confirmBeforeAction'
                            checked={selectedElement.confirmBeforeAction === true}
                            onChange={(e) =>
                              handleFieldChange(
                                selectedField,
                                e.target.checked,
                                'confirmBeforeAction'
                              )
                            }
                          />
                          <label className='form-check-label' htmlFor='confirmBeforeAction'>
                            Show confirmation popup
                          </label>
                        </div>
                      </div>
                      {selectedElement.confirmBeforeAction ? (
                        <>
                          <div className='mb-3'>
                            <label className='form-label fw-bold'>Popup title:</label>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={selectedElement.confirmTitle || ''}
                              onChange={(e) =>
                                handleFieldChange(selectedField, e.target.value, 'confirmTitle')
                              }
                            />
                          </div>
                          <div className='mb-3'>
                            <label className='form-label fw-bold'>Popup message:</label>
                            <textarea
                              className='form-control form-control-sm'
                              rows={3}
                              value={selectedElement.confirmMessage || ''}
                              onChange={(e) =>
                                handleFieldChange(selectedField, e.target.value, 'confirmMessage')
                              }
                            />
                          </div>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              )}
              {isButtonRow(selectedElement) && (
                <div className='bg-secondary p-2 text-dark bg-opacity-50'>
                  <div className='mb-3'>
                    <label className='form-label fw-bold'>Row alignment:</label>
                    <select
                      className='form-select form-select-sm'
                      value={selectedElement.alignment || 'end'}
                      onChange={(e) =>
                        handleFieldChange(selectedField, e.target.value, 'alignment')
                      }
                    >
                      <option value='start'>Left</option>
                      <option value='center'>Center</option>
                      <option value='end'>Right</option>
                    </select>
                  </div>
                  <div className='mb-3'>
                    <label className='form-label fw-bold'>Preview:</label>
                    <ButtonRowPreview element={selectedElement} />
                  </div>
                  {(selectedElement.buttons || []).map(
                    (button: ButtonConfig, buttonIndex: number) => (
                      <div
                        key={button.id || `row-btn-${buttonIndex}`}
                        className='border rounded p-3 mb-3 bg-white'
                      >
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <strong>Button {buttonIndex + 1}</strong>
                          <button
                            type='button'
                            className='btn btn-sm btn-outline-danger'
                            onClick={() => {
                              const nextButtons = (selectedElement.buttons || []).filter(
                                (_: ButtonConfig, index: number) => index !== buttonIndex
                              )
                              updateButtonRowButtons(nextButtons)
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        <div className='mb-2'>
                          <label className='form-label'>Type</label>
                          <select
                            className='form-select form-select-sm'
                            value={button.buttonAction}
                            onChange={(e) =>
                              handleButtonRowButtonChange(
                                buttonIndex,
                                'buttonAction',
                                e.target.value
                              )
                            }
                          >
                            {BUTTON_ACTIONS.map((action) => (
                              <option key={action.value} value={action.value}>
                                {action.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='mb-2'>
                          <label className='form-label'>Text</label>
                          <input
                            type='text'
                            className='form-control form-control-sm'
                            value={button.text}
                            onChange={(e) =>
                              handleButtonRowButtonChange(buttonIndex, 'text', e.target.value)
                            }
                          />
                        </div>
                        <div className='row g-2 mb-2'>
                          <div className='col-6'>
                            <label className='form-label'>Text color</label>
                            <input
                              type='color'
                              className='form-control form-control-color form-control-sm'
                              value={button.btnTxtColor}
                              onChange={(e) =>
                                handleButtonRowButtonChange(
                                  buttonIndex,
                                  'btnTxtColor',
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div className='col-6'>
                            <label className='form-label'>Button color</label>
                            <input
                              type='color'
                              className='form-control form-control-color form-control-sm'
                              value={button.btnBgColor}
                              onChange={(e) =>
                                handleButtonRowButtonChange(
                                  buttonIndex,
                                  'btnBgColor',
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                        {isPopupButtonAction(button.buttonAction) ? (
                          <PopupButtonFieldsEditor
                            config={button}
                            onFieldChange={(field, value) => {
                              const nextButtons = [...(selectedElement.buttons || [])]
                              nextButtons[buttonIndex] = {
                                ...nextButtons[buttonIndex],
                                [field]: value
                              }
                              updateButtonRowButtons(nextButtons)
                            }}
                          />
                        ) : null}
                        {!isPopupButtonAction(button.buttonAction) ? (
                          <>
                            <div className='form-check form-switch mb-2'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={button.confirmBeforeAction === true}
                                onChange={(e) =>
                                  handleButtonRowButtonChange(
                                    buttonIndex,
                                    'confirmBeforeAction',
                                    e.target.checked
                                  )
                                }
                              />
                              <label className='form-check-label'>Show confirmation popup</label>
                            </div>
                            {button.confirmBeforeAction ? (
                              <>
                                <div className='mb-2'>
                                  <label className='form-label'>Popup title</label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={button.confirmTitle || ''}
                                    onChange={(e) =>
                                      handleButtonRowButtonChange(
                                        buttonIndex,
                                        'confirmTitle',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                                <div className='mb-2'>
                                  <label className='form-label'>Popup message</label>
                                  <textarea
                                    className='form-control form-control-sm'
                                    rows={2}
                                    value={button.confirmMessage || ''}
                                    onChange={(e) =>
                                      handleButtonRowButtonChange(
                                        buttonIndex,
                                        'confirmMessage',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    )
                  )}
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={() =>
                      updateButtonRowButtons([
                        ...(selectedElement.buttons || []),
                        createDefaultButton('custom')
                      ])
                    }
                  >
                    Add button to row
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className='text-center'>No element selected</div>
        )}
      </div>
    </>
  )
}

export default FormFieldCustomization
